// IngestWorkflow model (PLAN §5, §12.5): which stages a file goes through, how far along it is, and where it ends.
// Pure logic, shared by the pipeline Worker (which runs the stages) and its tests.

import type { JobCounters, JobProgress, NotificationItem, StageDef } from './progress';
import { INGEST_STAGES } from './progress';
import { type ExtractionDepth, formatOf, type PipelineStatus, type ReviewMode } from './uploads';

/** What the queue hands the pipeline and the Workflow receives: keys, never payloads (PLAN §5). */
export interface IngestParams {
  documentId: string;
  r2Key: string;
}

export type IngestStageKey = 'reading' | 'ocr' | 'indexed' | 'extracting' | 'matching' | 'ready';

/** Every derived file of a document lives under this prefix, so cancelling deletes one prefix. */
export const documentPrefix = (id: string) => `documents/${id}/`;

/**
 * The stages a file goes through, in stepper order. `uploading` is already done when the Workflow starts.
 * OCR / transcription applies to images, audio and video, and to documents found to be scans while reading
 * (`scan`, task 12). Library-only files finish at "Indexed in Library" (PLAN §12.5).
 */
export function ingestStages(file: { filename: string; extractionDepth: ExtractionDepth }, opts: { scan?: boolean } = {}): StageDef[] {
  const family = formatOf(file.filename)?.family;
  const media = family === 'image' || family === 'audio' || family === 'video';
  const libraryOnly = file.extractionDepth === 'library';
  const dataset = family === 'dataset';
  return INGEST_STAGES.filter(s => {
    if (s.key === 'ocr') return media || !!opts.scan;
    if (s.key === 'extracting' || s.key === 'matching' || s.key === 'ready') return !libraryOnly;
    return true;
  }).map(s => (dataset && s.key === 'ready' ? { ...s, label: 'Ready to import' } : s));
}

/** CSV, XLSX and JSON files: structured data comes from the import wizard (task 25), not the AI extraction. */
export const isDatasetFile = (filename: string) => formatOf(filename)?.family === 'dataset';

/** Counters the card shows once extraction starts: mentions-only files find actors, deeper ones also relations and events. */
export function initialCounters(depth: ExtractionDepth): JobCounters {
  return depth === 'mentions' ? { actors: 0 } : { actors: 0, relations: 0, events: 0 };
}

/** Pipeline status once a stage has finished, when the stage moves the document on (PLAN §4 `pipeline_status`). */
export function statusAfterStage(key: IngestStageKey): PipelineStatus | null {
  if (key === 'indexed') return 'indexed';
  if (key === 'matching') return 'extracted';
  return null;
}

/**
 * Where a processed file ends. Library-only files are done once indexed. "Review now" files wait for review;
 * "review later" files go to the graph unreviewed (the skip-review commit, task 21) and wait in the Review Inbox.
 */
export function finalStatus(file: { extractionDepth: ExtractionDepth; reviewMode: ReviewMode; filename?: string }): PipelineStatus {
  if (file.extractionDepth === 'library') return 'library_only';
  // Datasets wait for the import wizard; the import moves them on (completed, or in review for free-text cells).
  if (file.filename && isDatasetFile(file.filename)) return 'extracted';
  return file.reviewMode === 'now' ? 'in_review' : 'skipped_review';
}

/** Statuses a running Workflow may move a document out of; anything else means someone else took it over. */
export const WORKFLOW_OWNED_STATUSES: PipelineStatus[] = ['processing', 'indexed', 'extracted'];

/** Overall percent from the stage position and the fraction done inside the current stage. */
export function ingestPercent(stages: StageDef[], stageIndex: number, fraction = 0): number {
  // Stage 0 (uploading) is complete before processing starts; the rest share the bar evenly.
  const work = stages.length - 1;
  if (work <= 0) return 100;
  const done = Math.max(0, stageIndex - 1) + Math.min(Math.max(fraction, 0), 1);
  return Math.min(99, Math.round(10 + (done / work) * 90));
}

/** Simple ETA: time so far, scaled to what's left. Undefined until there's enough to go on. */
export function ingestEta(startedAt: number, now: number, percent: number): number | undefined {
  if (percent <= 10) return undefined;
  const elapsed = now - startedAt;
  return Math.max(0, Math.round((elapsed / (percent - 10)) * (100 - percent)));
}

/** A failure the uploader can read: never raw error text, which could echo document content. */
export class IngestError extends Error {
  constructor(
    readonly userMessage: string,
    readonly retryable = true,
  ) {
    super(userMessage);
    this.name = 'IngestError';
  }
}

const STAGE_ACTIVITY: Record<string, string> = {
  reading: 'reading the file',
  ocr: 'recognising text and speech',
  indexed: 'adding it to the Library',
  extracting: 'extracting',
  matching: 'matching against existing actors',
};

export const genericFailure = (stage: StageDef | undefined) =>
  `Processing stopped while ${STAGE_ACTIVITY[stage?.key ?? ''] ?? 'processing'}. Nothing from this step was kept; you can retry.`;

/** The activity hub's RPC surface, as the pipeline Worker sees it (the class lives in the app Worker). */
export interface ActivityRpc {
  publish(job: JobProgress): Promise<void>;
  notify(userId: string, n: Omit<NotificationItem, 'id' | 'createdAt'>): Promise<NotificationItem>;
}
